import type { ServiceKey } from "@/lib/routes";
import {
  Code2,
  Globe,
  LayoutGrid,
  LineChart,
  Palette,
  Printer,
  Plug,
  Puzzle,
  Search,
  ShoppingCart,
  Layout,
  Megaphone,
  Users,
  type LucideIcon,
} from "lucide-react";

export type ServiceMeta = {
  key: ServiceKey;
  icon: LucideIcon;
  /** Lucide icon name (matches the shared serviceIcon map + CRM catalog). */
  iconName: string;
  /** Tailwind gradient classes used for the icon tile and detail hero. */
  gradient: string;
  /** Number of translated feature bullets available for this service. */
  featureCount: number;
  /**
   * Optional external booking URL. When set, the service detail page shows a
   * primary "book now" call-to-action that opens this URL in a new tab
   * (currently used for the coworking space, booked from lab.enkey.eu).
   */
  bookingUrl?: string;
};

// Presentation metadata for each service. Copy lives in the i18n files under
// the "services" namespace; this module only holds non-translatable visuals.
export const SERVICES: ServiceMeta[] = [
  {key:"software",icon:Code2,iconName:"Code2",gradient:"from-blue-500 to-indigo-500",featureCount:6},
  {
    key: "web",
    icon: Globe,
    iconName: "Globe",
    gradient: "from-violet-500 to-fuchsia-500",
    featureCount: 6,
  },
  {
    key: "webapp",
    icon: LayoutGrid,
    iconName: "LayoutGrid",
    gradient: "from-fuchsia-500 to-pink-500",
    featureCount: 6,
  },
  {
    key: "ecommerce",
    icon: ShoppingCart,
    iconName: "ShoppingCart",
    gradient: "from-cyan-500 to-blue-500",
    featureCount: 6,
  },
  {
    key: "marketing",
    icon: LineChart,
    iconName: "LineChart",
    gradient: "from-orange-500 to-pink-500",
    featureCount: 6,
  },
  {
    key: "seo",
    icon: Search,
    iconName: "Search",
    gradient: "from-emerald-500 to-teal-500",
    featureCount: 6,
  },
  {
    key: "graphics",
    icon: Palette,
    iconName: "Palette",
    gradient: "from-purple-500 to-indigo-500",
    featureCount: 6,
  },
  {
    key: "printing",
    icon: Printer,
    iconName: "Printer",
    gradient: "from-blue-600 to-cyan-500",
    featureCount: 6,
  },
  {
    key: "coworking",
    icon: Users,
    iconName: "Users",
    gradient: "from-rose-500 to-orange-500",
    featureCount: 6,
  },
  {
    key: "wpplugin",
    icon: Plug,
    iconName: "Plug",
    gradient: "from-blue-500 to-indigo-500",
    featureCount: 6,
  },
  {
    key: "psmodule",
    icon: Puzzle,
    iconName: "Puzzle",
    gradient: "from-teal-500 to-emerald-500",
    featureCount: 6,
  },
  {
    key: "wptheme",
    icon: Layout,
    iconName: "Layout",
    gradient: "from-pink-500 to-rose-500",
    featureCount: 6,
  },
  {
    key: "tessidra",
    icon: Megaphone,
    iconName: "Megaphone",
    gradient: "from-amber-500 to-orange-600",
    featureCount: 6,
    bookingUrl: "https://www.tessidra.it",
  },
];

export function getServiceMeta(key: ServiceKey): ServiceMeta {
  const found = SERVICES.find((s) => s.key === key);
  // SERVICES covers every ServiceKey, so this is always defined.
  return found ?? SERVICES[0];
}
